/**
 * Which `CampaignContentBlock` categories each frontier step is responsible for, so the step editor
 * can embed a focused, friendly slice of the content corpus (via a scoped `ContentBlocksEditor`) right
 * next to the step's own fields — instead of making the admin hunt for the correct categories in the
 * campaign-wide "Content blocks" panel. Every category still lives (and can also be edited) in that
 * campaign-wide panel; this map is purely for convenient, discoverable per-step authoring.
 *
 * Keyed by `metadata.runnerKind`. Only kinds that own content-block-driven data appear (profiling is
 * absent — its "Connect your tools" copy is hard-coded props, not content blocks).
 *
 * Coverage notes (from tracing every resolver to its consumer):
 *   - Some categories drive the *on-screen* runner UI (e.g. scenario_question, recommended_question).
 *   - Others feed the exported report, the Guide chatbot, or the AI drafts rather than a visible panel
 *     (e.g. agenda, discovery_question, prereq_question, gap_row, next_step). Those are labelled so the
 *     admin knows where the value surfaces, but they are still fully editable here.
 *   - "Recommended plays" (deep_dive_play) are NOT content blocks — they are `CampaignCapability` rows,
 *     edited in the campaign's Capabilities section, so they are deliberately absent here.
 *   - `deep_dive_question` is edited via the step editor's separate export-only "Questions" panel, not
 *     here (it feeds only the exported report — see StepQuestionsFields' deepdive note).
 */
export interface StepContentCategoryMap {
  /**
   * Product-neutral name for this content template, shown in the step editor's "Content template"
   * picker. A template is a data discriminator only — it selects which composition layout + content
   * manifest a `content` step authors — so its label must describe what the content *does*, never the
   * campaign it came from (no "Frontier"), keeping the bucket generic and reusable across campaigns.
   */
  label: string;
  /**
   * Optional nested-tree editor to render *before* the flat categories, keyed into
   * `NESTED_CONTENT_TREES` (see `nestedContentTrees.ts`). Use for hierarchical content the flat
   * one-category-at-a-time editor can't express well (e.g. the deep-dive Scenario ▸ Phase ▸ Section ▸
   * Question tree). The `categories` below then cover only the step's *flat* remainder.
   */
  tree?: string;
  /** Flat categories to expose (one at a time), in on-screen / authoring order. */
  categories: string[];
  /** Friendly display labels for the category dropdown, keyed by raw category. */
  labels: Record<string, string>;
  /** Panel header. */
  title: string;
  /** Panel sub-header. */
  subtitle: string;
}

/** Shared tail appended to every panel subtitle — the save-semantics caveat. */
const SAVE_NOTE =
  ' These are campaign-wide content blocks and save immediately — independently of this step form’s Save/Cancel.';

/**
 * Optional content sections an author can ADD to a content step (WS3). None are present by default —
 * a section appears only when the author opts in, keeping every step's config minimal and relevant.
 * Each entry names a content-block `category` (edited via `ContentBlocksEditor` + its `CATEGORY_SCHEMAS`
 * schema) and the composition `primitive` that renders it at runtime (added to the step's layout via
 * `StepLayoutComposer`). Enabling a section here is DB-driven: it reveals the category's editor and is
 * remembered on the step (`metadata.contentSections`); the runtime renders nothing until blocks exist
 * AND the primitive is placed in the step's layout.
 */
export interface OptionalContentSection {
  /** Content-block category (must have a `CATEGORY_SCHEMAS` entry for the friendly editor). */
  category: string;
  /** Composition primitive key that renders this category (must be registered in `primitives/core`). */
  primitive: string;
  label: string;
  title: string;
  subtitle: string;
}

export const OPTIONAL_CONTENT_SECTIONS: OptionalContentSection[] = [
  {
    category: 'faq',
    primitive: 'faq',
    label: 'FAQ',
    title: 'FAQ content',
    subtitle:
      'Optional reference Q&A shown on this step. Pairs with the same Group are grouped under that heading. Add the "faq" primitive to this step’s layout (below) to render them.' +
      SAVE_NOTE,
  },
];

export const STEP_CONTENT_CATEGORIES: Record<string, StepContentCategoryMap> = {
  profiling: {
    label: 'Tool connection',
    categories: ['connect_tool'],
    labels: {
      connect_tool: 'Connect your tools cards',
    },
    title: 'Profiling content',
    subtitle:
      'The "Connect your tools" cards shown in the Profiling step (tool name, URL, how-to steps, button labels).' +
      SAVE_NOTE,
  },
  prepare: {
    label: 'Preparation & readiness',
    categories: ['stakeholder_priority', 'prereq_question'],
    labels: {
      stakeholder_priority: 'Stakeholder priority labels (must / should / nice)',
      prereq_question: 'Prerequisite questions (Guide & AI)',
    },
    title: 'Prepare content',
    subtitle:
      'The stakeholder-priority labels on each roster row and the prerequisite questions the Guide/AI use.' +
      SAVE_NOTE,
  },
  propose: {
    label: 'Discovery & proposal',
    categories: ['recommended_question', 'iq_scenario', 'discovery_question'],
    labels: {
      recommended_question: 'Recommended discovery questions',
      iq_scenario: 'Cisco IQ scenarios',
      discovery_question: 'Discovery questions (Guide & AI)',
    },
    title: 'Propose content',
    subtitle:
      'The recommended discovery questions shown on-screen, the Cisco IQ scenario states, and the discovery questions the Guide/AI draw on.' +
      SAVE_NOTE,
  },
  workshop: {
    label: 'Workshop session',
    categories: ['agenda'],
    labels: {
      agenda: 'Workshop agenda items',
    },
    title: 'Workshop content',
    subtitle:
      'The workshop agenda items (title, duration, description) used in the exported summary. The slides themselves are mapped under Files above.' +
      SAVE_NOTE,
  },
  deepdive: {
    label: 'Deep-dive guide',
    // The Scenario ▸ Phase ▸ Section ▸ Question hierarchy is edited as a nested tree; the flat
    // categories below are the deep-dive content that isn’t part of that tree.
    tree: 'deepdive',
    categories: ['iq_scenario', 'deep_dive_stakeholder', 'pushback', 'sequencing', 'closing'],
    labels: {
      iq_scenario: 'IQ scenarios',
      deep_dive_stakeholder: 'Stakeholder roles',
      pushback: 'Handling pushback',
      sequencing: 'Sequencing notes (Guide)',
      closing: 'Closing items (Guide)',
    },
    title: 'Other deep-dive content',
    subtitle:
      'The Cisco IQ scenario states, the stakeholder roles a question’s “who to ask” points at, the “Handling pushback” objections/responses, the sequencing tips, and the closing items (the latter two feed the exported Guide). The scenarios, phases, sections and banded questions themselves are edited in the nested tree above.' +
      SAVE_NOTE,
  },
  recommendation: {
    label: 'Recommendation report',
    categories: ['gap_row', 'next_step'],
    labels: {
      gap_row: 'Gap analysis rows',
      next_step: 'Next steps',
    },
    title: 'Recommendation content',
    subtitle:
      'The gap-analysis rows (category, current state, gap, recommended action) and next-step lines the recommendation report is built from. Each row’s live install-base figure remains code-driven. The report template itself — its sections, prose, charts and per-delivery substitution tokens — is authored in the Report template panel on this step.' +
      SAVE_NOTE,
  },
};

/**
 * The "Content template" picker options for a `content` step — a leading "None" (a plain content step)
 * followed by every template in `STEP_CONTENT_CATEGORIES`, in declaration order, with its product-neutral
 * `label`. Derived from the manifest so adding a new content template is a single data edit here (its
 * label, categories and panels) with zero step-editor code changes — the whole point of the generic
 * content bucket.
 */
export const CONTENT_VARIANTS: { value: string; label: string }[] = [
  { value: '', label: 'None (standard content step)' },
  ...Object.entries(STEP_CONTENT_CATEGORIES).map(([value, m]) => ({ value, label: m.label })),
];
